// The item of an Item form (its initial values), derived from the stored row
// at each read of the row: the open, and every re-read (db.dispatchAll()
// after a load of the worker, a restart, a notification of the table).
// Formik reinitialises the form (enableReinitialize) when the item it gets
// differs from its initial values, discarding its unsaved edits: a re-read
// of a row that did not change must give the same item.
import isEqual from "lodash.isequal";
import { withStoredSubBlockIds } from "../components/block/subBlockIds";

// A block, not a page: the sub-blocks of a page ("nested", keyed by `id`, the
// key of their layout) have no _id in most stored pages, and must not get
// one: the block validation (also the variant page's) checks only the
// sub-blocks with an _id (validation.js, setError), and these have no
// results.contentType, so every such page would become invalid.
const isBlock = (accessor, row) => accessor === "blocks" && !row.isPage;

// An endpoint stored without prevReference: the endpoint form sets it to
// the reference when it opens (endpoint.jsx), which made the form modified
// as soon as it opened. The item has it already.
const withPrevReference = (row) =>
  !row.prevReference && row.reference
    ? { ...row, prevReference: row.reference }
    : row;

// What the form adds to the stored row of `accessor`, the same at every read
// of the same row (deterministic): a block gets an _id on each sub-block (the
// editor finds a sub-block by it: the hand-off of a conditions box); an
// endpoint without prevReference gets its reference as prevReference (saved
// with the next Save of the form). Applied again to its result, it adds
// nothing. The stash of the form applies it to the rows it compares with
// (stashForm's derive), so that what it adds is not a change.
export function deriveRow(accessor, row) {
  if (!row) return row;
  if (accessor === "endpoints") return withPrevReference(row);
  return isBlock(accessor, row) ? withStoredSubBlockIds(row) : row;
}

// The item of `accessor` from its stored row: the row itself when there is
// nothing to add. A copy (?copy=<id>): the copied row without its title,
// description and refs; a copy of a block has new sub-block ids, from `id`
// (the id of the new item: the same at every read of the copied row). The
// sub-blocks used to get them when they mount (subBlock.jsx), which misses
// the first one: it mounts before the copied row arrives. Always a new
// object when it differs from the row: the row read is not changed.
export function itemFromRow(accessor, row, { copy = false, id } = {}) {
  if (!row || !copy) return deriveRow(accessor, row);

  const item = { ...row, title: "", description: "", refs: {} };
  if (isBlock(accessor, row) && Array.isArray(row.blocks))
    item.blocks = row.blocks.map((block, index) =>
      block ? { ...block, _id: `${id}_${index}` } : block
    );
  return accessor === "endpoints" ? withPrevReference(item) : item;
}

// Same item, whatever refManager computed on the stored row (refsComputed):
// it recomputes it in place, without a notification (refManager.refill()
// after a delete, fillBelongs after a put), and with the titles of the rows
// the row requires; a re-read then brings the same row with other
// refsComputed.
export function sameItem(a, b) {
  if (!a || !b) return a === b;
  const { refsComputed: ra, ...restA } = a;
  const { refsComputed: rb, ...restB } = b;
  return isEqual(restA, restB);
}
